import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, TrendingUp, TrendingDown, Minus, Heart, Square, Share2, BarChart2, Volume2, VolumeX } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../store/authStore';
import { useDeviceWebSocket } from '../hooks/useDeviceWebSocket';
import { useHeartbeatSound } from '../hooks/useHeartbeatSound';
import { getSessions, endSession } from '../api/sessions';
export function MonitorScreen() {
  const navigate = useNavigate();
  const {
    token
  } = useAuth();
  const {
    isConnected,
    isBeltOnline,
    bpm: legacyBpm,
    fetalBpm,
    maternalBpm,
    piezoValue,
    sensor2Value
  } = useDeviceWebSocket(token);
  const rawBpm = fetalBpm ?? legacyBpm;
  const {
    soundEnabled,
    toggleSound
  } = useHeartbeatSound(rawBpm, isConnected);
  // Session State
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [showEndSession, setShowEndSession] = useState(false);
  const [isEndingSession, setIsEndingSession] = useState(false);
  // Stats State
  const [bpm, setBpm] = useState<number | null>(null);
  const [minBpm, setMinBpm] = useState<number | null>(null);
  const [maxBpm, setMaxBpm] = useState<number | null>(null);
  const [avgBpm, setAvgBpm] = useState<number | null>(null);
  const bpmHistoryRef = useRef<number[]>([]);
  const lastRenderedBpmRef = useRef<number | null>(null);
  const lastBpmUpdateTimeRef = useRef<number>(0);
  // Trend State
  const [trend, setTrend] = useState<'up' | 'down' | 'flat'>('flat');
  // Signal Quality State
  const [signalQuality, setSignalQuality] = useState<'good' | 'fair' | 'poor' | 'none'>('none');
  const recentPiezoRef = useRef<number[]>([]);
  // Baby Activity State
  const [kickCount, setKickCount] = useState(0);
  const [showKick, setShowKick] = useState(false);
  const [showKickText, setShowKickText] = useState(false);
  const lastKickTimeRef = useRef(0);
  // Canvas Refs
  const mainCanvasRef = useRef<HTMLCanvasElement>(null);
  const miniCanvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  // Data Buffers
  const mainBufferRef = useRef<number[]>(Array(400).fill(0));
  const miniBufferRef = useRef<number[]>(Array(100).fill(0));
  const scrollOffsetRef = useRef<number>(0);
  // Landscape State
  const [isLandscape, setIsLandscape] = useState(false);
  const [showLandscapeStats, setShowLandscapeStats] = useState(true);
  // --- Session Management ---
  useEffect(() => {
    if (!token) return;
    const checkSession = async () => {
      try {
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        const sessions = await getSessions(token, {
          from: startOfToday.toISOString()
        });
        const active = sessions.find((s: any) => s.sessionStatus === 'active');
        if (active) {
          setActiveSessionId(active.id);
        }
      } catch (err) {
        // console.error('Failed to check active session', err)
      }
    };
    checkSession();
  }, [token]);
  const handleEndSession = async () => {
    if (!token || !activeSessionId) return;
    setIsEndingSession(true);
    try {
      await endSession(token, activeSessionId);
      setShowEndSession(false);
      navigate('/app/history');
    } catch (err) {
      // console.error('Failed to end session', err)
      setIsEndingSession(false);
    }
  };
  // --- Orientation Handling ---
  useEffect(() => {
    const handleResize = () => {
      setIsLandscape(window.innerWidth > window.innerHeight && window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // --- Data Processing ---
  useEffect(() => {
    if (!isConnected) {
      setSignalQuality('none');
      return;
    }
    // Update Main Buffer
    mainBufferRef.current.push(piezoValue);
    if (mainBufferRef.current.length > 400) mainBufferRef.current.shift();
    // Update Mini Buffer
    miniBufferRef.current.push(sensor2Value);
    if (miniBufferRef.current.length > 100) miniBufferRef.current.shift();
    // Calculate Signal Quality
    recentPiezoRef.current.push(piezoValue);
    if (recentPiezoRef.current.length > 20) recentPiezoRef.current.shift();
    if (recentPiezoRef.current.length === 20) {
      const min = Math.min(...recentPiezoRef.current);
      const max = Math.max(...recentPiezoRef.current);
      const range = max - min;
      if (range > 200 && range < 3000) setSignalQuality('good');else if (range >= 50 && range <= 200) setSignalQuality('fair');else setSignalQuality('poor');
    }
    // Kick Detection
    if (isConnected && piezoValue > 2000) {
      const now = Date.now();
      if (now - lastKickTimeRef.current > 1000) {
        lastKickTimeRef.current = now;
        setKickCount(prev => prev + 1);
        setShowKick(true);
        setShowKickText(true);
        setTimeout(() => setShowKick(false), 300);
        setTimeout(() => setShowKickText(false), 1500);
      }
    }
  }, [piezoValue, sensor2Value, isConnected]);
  // Throttled BPM Updates & Stats
  useEffect(() => {
    if (!rawBpm || !isConnected) return;
    const now = Date.now();
    const timeSinceLastUpdate = now - lastBpmUpdateTimeRef.current;
    const valueDiff = lastRenderedBpmRef.current ? Math.abs(rawBpm - lastRenderedBpmRef.current) : 100;
    if (valueDiff > 1 || timeSinceLastUpdate > 500) {
      setBpm(rawBpm);
      lastRenderedBpmRef.current = rawBpm;
      lastBpmUpdateTimeRef.current = now;
      // Update Stats
      setMinBpm(prev => prev === null ? rawBpm : Math.min(prev, rawBpm));
      setMaxBpm(prev => prev === null ? rawBpm : Math.max(prev, rawBpm));
      bpmHistoryRef.current.push(rawBpm);
      const sum = bpmHistoryRef.current.reduce((a, b) => a + b, 0);
      setAvgBpm(Math.round(sum / bpmHistoryRef.current.length));
      // Update Trend
      const history = bpmHistoryRef.current;
      if (history.length >= 5) {
        const recent = history.slice(-5);
        let increasing = 0;
        let decreasing = 0;
        for (let i = 1; i < recent.length; i++) {
          if (recent[i] > recent[i - 1]) increasing++;
          if (recent[i] < recent[i - 1]) decreasing++;
        }
        if (increasing >= 3) setTrend('up');else if (decreasing >= 3) setTrend('down');else setTrend('flat');
      }
    }
  }, [rawBpm, isConnected]);
  // --- Beating Heart Animation ---
  const [beatPulse, setBeatPulse] = useState(false);
  useEffect(() => {
    if (!isConnected || !bpm) return;
    const intervalMs = 60000 / bpm;
    const interval = setInterval(() => {
      setBeatPulse(true);
      setTimeout(() => setBeatPulse(false), 400); // Reset after animation
    }, intervalMs);
    return () => clearInterval(interval);
  }, [bpm, isConnected]);
  // --- Canvas Rendering ---
  useEffect(() => {
    const mainCanvas = mainCanvasRef.current;
    const miniCanvas = miniCanvasRef.current;
    if (!mainCanvas || !miniCanvas) return;
    const mainCtx = mainCanvas.getContext('2d');
    const miniCtx = miniCanvas.getContext('2d');
    if (!mainCtx || !miniCtx) return;
    let time = 0;
    const draw = () => {
      // Handle Retina Display
      const dpr = window.devicePixelRatio || 1;
      // Main Canvas Setup
      const mainRect = mainCanvas.parentElement?.getBoundingClientRect();
      if (mainRect) {
        if (mainCanvas.width !== mainRect.width * dpr || mainCanvas.height !== mainRect.height * dpr) {
          mainCanvas.width = mainRect.width * dpr;
          mainCanvas.height = mainRect.height * dpr;
        }
      }
      // Mini Canvas Setup
      const miniRect = miniCanvas.parentElement?.getBoundingClientRect();
      if (miniRect) {
        if (miniCanvas.width !== miniRect.width * dpr || miniCanvas.height !== miniRect.height * dpr) {
          miniCanvas.width = miniRect.width * dpr;
          miniCanvas.height = miniRect.height * dpr;
        }
      }
      const mWidth = mainCanvas.width / dpr;
      const mHeight = mainCanvas.height / dpr;
      // Clear Main Canvas
      mainCtx.save();
      mainCtx.scale(dpr, dpr);
      mainCtx.fillStyle = '#1A1211';
      mainCtx.fillRect(0, 0, mWidth, mHeight);
      // Draw Grid
      mainCtx.strokeStyle = 'rgba(255,255,255,0.04)';
      mainCtx.lineWidth = 1;
      mainCtx.beginPath();
      // Horizontals
      for (let i = 1; i < 5; i++) {
        const y = mHeight / 5 * i;
        mainCtx.moveTo(0, y);
        mainCtx.lineTo(mWidth, y);
      }
      // Verticals
      const scrollMod = scrollOffsetRef.current % 50;
      for (let x = -scrollMod; x < mWidth; x += 50) {
        mainCtx.moveTo(x, 0);
        mainCtx.lineTo(x, mHeight);
      }
      mainCtx.stroke();
      if (!isConnected) {
        // Disconnected State
        time += 0.02;
        mainCtx.beginPath();
        const yCenter = mHeight / 2;
        for (let x = 0; x < mWidth; x++) {
          // Cosmetic pulse traveling right to left
          const pulseX = mWidth - time * 100 % (mWidth * 1.5);
          const dist = Math.abs(x - pulseX);
          const pulseY = dist < 50 ? Math.cos(dist * Math.PI / 100) * 30 : 0;
          if (x === 0) mainCtx.moveTo(x, yCenter - pulseY);else mainCtx.lineTo(x, yCenter - pulseY);
        }
        mainCtx.strokeStyle = 'rgba(107, 33, 232, 0.3)';
        mainCtx.lineWidth = 2;
        mainCtx.stroke();
      } else {
        // Connected State - Draw Waveform
        const data = mainBufferRef.current;
        const step = mWidth / (data.length - 1);
        const paddingY = mHeight * 0.1;
        const usableHeight = mHeight - paddingY * 2;
        const drawLine = (lineWidth: number, color: string) => {
          mainCtx.beginPath();
          for (let i = 0; i < data.length; i++) {
            // Smooth scrolling offset
            const x = i * step - scrollOffsetRef.current % step;
            const y = mHeight - paddingY - data[i] / 4095 * usableHeight;
            if (i === 0) mainCtx.moveTo(x, y);else mainCtx.lineTo(x, y);
          }
          mainCtx.strokeStyle = color;
          mainCtx.lineWidth = lineWidth;
          mainCtx.lineJoin = 'round';
          mainCtx.stroke();
        };
        // Triple Glow
        drawLine(8, 'rgba(107, 33, 232, 0.08)');
        drawLine(4, 'rgba(107, 33, 232, 0.25)');
        drawLine(1.5, '#6B21E8');
        // Leading Dot
        const lastX = (data.length - 1) * step - scrollOffsetRef.current % step;
        const lastY = mHeight - paddingY - data[data.length - 1] / 4095 * usableHeight;
        mainCtx.beginPath();
        mainCtx.arc(lastX, lastY, 12, 0, Math.PI * 2);
        mainCtx.fillStyle = 'rgba(107, 33, 232, 0.4)';
        mainCtx.fill();
        mainCtx.beginPath();
        mainCtx.arc(lastX, lastY, 6, 0, Math.PI * 2);
        mainCtx.fillStyle = '#6B21E8';
        mainCtx.fill();
        // Increment scroll
        scrollOffsetRef.current += 1;
      }
      mainCtx.restore();
      // --- Mini Canvas (Uterine Activity) ---
      const miniW = miniCanvas.width / dpr;
      const miniH = miniCanvas.height / dpr;
      miniCtx.save();
      miniCtx.scale(dpr, dpr);
      miniCtx.clearRect(0, 0, miniW, miniH);
      if (isConnected) {
        const miniData = miniBufferRef.current;
        const miniStep = miniW / (miniData.length - 1);
        miniCtx.beginPath();
        for (let i = 0; i < miniData.length; i++) {
          const x = i * miniStep;
          const y = miniH - miniData[i] / 4095 * miniH;
          if (i === 0) miniCtx.moveTo(x, y);else miniCtx.lineTo(x, y);
        }
        miniCtx.strokeStyle = '#00D4D4';
        miniCtx.lineWidth = 1.5;
        miniCtx.lineJoin = 'round';
        miniCtx.stroke();
      }
      miniCtx.restore();
      animationRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isConnected]);
  // --- Render Helpers ---
  const getSignalQualityProps = () => {
    switch (signalQuality) {
      case 'good':
        return {
          color: '#00D4D4',
          width: '100%',
          label: 'Good'
        };
      case 'fair':
        return {
          color: '#F59E0B',
          width: '60%',
          label: 'Fair'
        };
      case 'poor':
        return {
          color: '#E8735A',
          width: '30%',
          label: 'Poor'
        };
      default:
        return {
          color: '#8A8A8A',
          width: '0%',
          label: 'No Signal'
        };
    }
  };
  const sqProps = getSignalQualityProps();
  return <main className="fixed inset-0 bg-[#1A1211] overflow-hidden flex flex-col lg:flex-row z-[100]" data-id="element-1443" data-mp="2xh4n9syz99vf">
      {/* TOP BAR (Hidden in landscape mobile) */}
      {!isLandscape && <div className="absolute top-0 left-0 right-0 h-[80px] pt-[env(safe-area-inset-top)] px-4 flex items-center justify-between z-20 bg-gradient-to-b from-[#1A1211]/80 to-transparent" data-id="element-1444" data-mp="24za513ftk077">
          <button onClick={() => navigate('/app/dashboard')} className="w-[40px] h-[40px] rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" aria-label="Go back" data-id="element-1445" data-mp="1sgwqqvrf8pxm">
            <ChevronDown size={24} className="text-white/80" data-id="element-1446" data-mp="1ilcv8agrrt9e" />
          </button>
          <span className="text-white text-[16px] font-semibold" data-id="element-1447" data-mp="1ej81lbqbmwmr">
            Live Monitor
          </span>
          <div className="w-[100px] flex justify-end items-center space-x-3" data-id="element-1448" data-mp="19pncyaambf08">
            <button onClick={toggleSound} className="w-[40px] h-[40px] rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" aria-label={soundEnabled ? 'Mute heartbeat' : 'Play heartbeat'} data-id="element-1449" data-mp="14sn7a0hoke9n">
              {soundEnabled ? <Volume2 size={20} className="text-accent" data-id="element-1450" data-mp="qsvws9qdhg0e" /> : <VolumeX size={20} className="text-white/80" data-id="element-1451" data-mp="1fk1jweqxs0t2" />}
            </button>
            {activeSessionId && <div className="flex items-center space-x-2" data-id="element-1452" data-mp="1kjnz2tqbrr4i">
                <div className="w-2 h-2 rounded-full bg-danger animate-pulse" data-id="element-1453" data-mp="16pn9oql9jggt" />
                <span className="text-white/80 text-[13px] hidden sm:inline" data-id="element-1454" data-mp="1ar8f5qvgwsgz">
                  Recording
                </span>
              </div>}
          </div>
        </div>}

      {/* LEFT PANEL / MAIN CANVAS */}
      <div className={`relative w-full ${isLandscape ? 'h-full' : 'h-[55vh] lg:h-full lg:w-[65%]'} flex-shrink-0`} onClick={() => isLandscape && setShowLandscapeStats(!showLandscapeStats)} data-id="element-1455" data-mp="11sn0w7x13zm0">
        <div className="absolute inset-0 w-full h-full" data-id="element-1456" data-mp="2i5lkp0004cko">
          <canvas ref={mainCanvasRef} className="w-full h-full block" role="img" aria-label="Live fetal heart rate waveform" data-id="element-1457" data-mp="2i63elpz6yepf" />
        </div>

        {/* Gradient Overlay for mobile/tablet portrait */}
        {!isLandscape && <div className="absolute bottom-0 left-0 right-0 h-[100px] bg-gradient-to-b from-transparent to-[#1A1211] lg:hidden" data-id="element-1458" data-mp="6jjenx47eccp" />}
      </div>

      {/* RIGHT PANEL / STATS & CONTROLS */}
      {(!isLandscape || showLandscapeStats) && <div className={`
          ${isLandscape ? 'absolute bottom-4 right-4 bg-[#221A19]/90 backdrop-blur-md rounded-[24px] p-4 flex flex-row items-center space-x-6' : 'flex-1 flex flex-col bg-[#1A1211] lg:bg-[#221A19] z-10 px-6 pb-6 lg:p-8 overflow-y-auto'}
        `} data-id="element-1459" data-mp="u7swknyaqfyk">
          {/* BPM Hero Display */}
          <div className={`flex flex-col ${isLandscape ? 'items-start' : 'items-center lg:items-start lg:mt-12'}`} data-id="element-1460" data-mp="y963l1d4kp72">
            <div className="flex items-start" data-id="element-1461" data-mp="35047g0u9j5fu">
              <motion.div key={bpm} initial={{
            y: -4,
            scale: 1.05
          }} animate={{
            y: 0,
            scale: 1
          }} transition={{
            duration: 0.25
          }} className={`font-bold text-primary leading-none ${isLandscape ? 'text-[48px]' : 'text-[72px] lg:text-[96px]'}`} aria-live="polite" aria-label={`Current BPM: ${isConnected && bpm ? bpm : 'Unknown'}`} data-id="element-1462" data-mp="301as02vf14d1">
                {isConnected && bpm ? bpm : '--'}
              </motion.div>
              <div className="ml-2 mt-2 flex flex-col items-center" data-id="element-1463" data-mp="1rt8rr1ul13mn">
                {trend === 'up' && <TrendingUp size={24} className="text-accent mb-1" data-id="element-1464" data-mp="21leqqvj94lbc" />}
                {trend === 'down' && <TrendingDown size={24} className="text-danger mb-1" data-id="element-1465" data-mp="25nrnwvjxnawi" />}
                {trend === 'flat' && <Minus size={24} className="text-white/40 mb-1" data-id="element-1466" data-mp="2ammhmsjfujve" />}
              </div>
            </div>
            <span className="text-white/50 text-[16px] mt-1" data-id="element-1467" data-mp="3v1r7sut6onw3">Fetal BPM</span>

            <div className={`mt-4 inline-flex items-center gap-2 rounded-pill px-3 py-1.5 text-[13px] font-semibold ${isBeltOnline ? 'bg-success/15 text-success' : 'bg-white/10 text-white/55'}`} role="status" aria-live="polite" data-id="element-1468" data-mp="3q3pp4azo6ql5">
              <span className={`relative flex h-2 w-2 ${isBeltOnline ? '' : 'opacity-70'}`} data-id="element-1469" data-mp="hnxofzlnav2a">
                {isBeltOnline && <span className="absolute inline-flex h-full w-full rounded-full bg-success opacity-75 animate-ping" data-id="element-1470" data-mp="8wnif8tft6gp" />}
                <span className={`relative inline-flex h-2 w-2 rounded-full ${isBeltOnline ? 'bg-success' : 'bg-white/40'}`} data-id="element-1471" data-mp="3x14sy9p5o7d" />
              </span>
              {isBeltOnline ? 'Belt Connected' : 'Belt Offline'}
            </div>

            <div className="mt-4 rounded-[16px] border border-white/10 bg-white/5 px-4 py-3 min-w-[156px]" data-id="element-1472" data-mp="4yewj113spth">
              <span className="block text-[11px] text-white/50 uppercase tracking-[1px] mb-1" data-id="element-1473" data-mp="9pwwblre4kcv">
                Maternal BPM
              </span>
              <span className="text-[28px] font-semibold text-accent leading-none tabular-nums" aria-live="polite" aria-label={`Maternal BPM: ${isConnected && maternalBpm !== null ? maternalBpm : 'Unknown'}`} data-id="element-1474" data-mp="3r7y5yil1c170">
                {isConnected && maternalBpm !== null ? maternalBpm : '--'}
              </span>
            </div>

            {/* Beating Heart */}
            {!isLandscape && <div className="relative mt-6 mb-8 flex items-center justify-center lg:justify-start w-full" data-id="element-1475" data-mp="ipjy48o17s6g">
                <motion.div animate={beatPulse ? {
            scale: [1, 1.3, 1]
          } : {
            scale: 1
          }} transition={{
            duration: 0.2,
            ease: 'easeInOut'
          }} className={`relative z-10 ${!isConnected ? 'opacity-40' : ''}`} data-id="element-1476" data-mp="2zdw4s4y66yp2">
                  <Heart size={40} className="text-primary fill-primary" data-id="element-1477" data-mp="30wnrh595vg03" />
                </motion.div>

                {/* Ripple */}
                {beatPulse && isConnected && <motion.div initial={{
            scale: 1,
            opacity: 0.5
          }} animate={{
            scale: 2.5,
            opacity: 0
          }} transition={{
            duration: 0.4
          }} className="absolute w-[40px] h-[40px] rounded-full border-2 border-primary lg:ml-0" data-id="element-1478" data-mp="288ryobmpq88l" />}

                {!isConnected && <div className="absolute top-12 flex items-center space-x-2" data-id="element-1479" data-mp="37lpcmc3p8bii">
                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" data-id="element-1480" data-mp="32mvug6dgvwc0" />
                    <span className="text-white/50 text-[13px]" data-id="element-1481" data-mp="3gi7xr25ouv4j">
                      Reconnecting...
                    </span>
                  </div>}
              </div>}
          </div>

          {/* Stats Row */}
          <div className={`flex ${isLandscape ? 'space-x-6 ml-6 border-l border-white/10 pl-6' : 'justify-between w-full lg:flex-col lg:space-y-6 lg:mt-8'}`} data-id="element-1482" data-mp="3leeymgj89gfm">
            <div className="flex flex-col" data-id="element-1483" data-mp="3vde7o07wne35">
              <span className="text-[12px] text-white/50 uppercase tracking-[1px] mb-1" data-id="element-1484" data-mp="3vefh6099vsir">
                Min
              </span>
              <span className="text-[20px] font-semibold text-white" data-id="element-1485" data-mp="20zmrsiyzfxz9">
                {minBpm || '--'}
              </span>
            </div>
            <div className="flex flex-col" data-id="element-1486" data-mp="1qwr8tlvaibwh">
              <span className="text-[12px] text-white/50 uppercase tracking-[1px] mb-1" data-id="element-1487" data-mp="1qy29vdxcp5jw">
                Max
              </span>
              <span className="text-[20px] font-semibold text-white" data-id="element-1488" data-mp="1h6eo9tnh6iqc">
                {maxBpm || '--'}
              </span>
            </div>
            <div className="flex flex-col" data-id="element-1489" data-mp="1m3eqtul493cp">
              <span className="text-[12px] text-white/50 uppercase tracking-[1px] mb-1" data-id="element-1490" data-mp="177ytngp8orc3">
                Avg
              </span>
              <span className="text-[20px] font-semibold text-white" data-id="element-1491" data-mp="1c7uy1h1umks6">
                {avgBpm || '--'}
              </span>
            </div>
          </div>

          {/* Signal Quality & Uterine Activity (Hidden in landscape) */}
          {!isLandscape && <div className="w-full mt-8 lg:mt-12 space-y-8" data-id="element-1492" data-mp="2sl1vw4cok93m">
              {/* Signal Quality */}
              <div data-id="element-1493" data-mp="2xk4w9bhptr59">
                <span className="text-[12px] text-white/50 uppercase tracking-[1px] block mb-2" data-id="element-1494" data-mp="2xkekoun43q8u">
                  Signal Quality
                </span>
                <div className="w-full h-[6px] bg-white/10 rounded-pill overflow-hidden mb-2" data-id="element-1495" data-mp="2v3dh94dhwjj9">
                  <div className="h-full rounded-pill transition-all duration-500" style={{
              width: sqProps.width,
              backgroundColor: sqProps.color
            }} data-id="element-1496" data-mp="2r5z9lh5gqjtd" />
                </div>
                <span className="text-[13px]" style={{
            color: sqProps.color
          }} data-id="element-1497" data-mp="3esnso667kibk">
                  {sqProps.label}
                </span>
              </div>

              {/* Uterine Activity */}
              <div data-id="element-1498" data-mp="3041g2c7yu4o1">
                <span className="text-[12px] text-white/50 uppercase tracking-[1px] block mb-2" data-id="element-1499" data-mp="302glwnpkcnqp">
                  Uterine Activity
                </span>
                <div className="w-full h-[40px] relative mb-1" data-id="element-1500" data-mp="3ss72d3eo41pz">
                  <canvas ref={miniCanvasRef} className="absolute inset-0 w-full h-full" role="img" aria-label="Live uterine activity waveform" data-id="element-1501" data-mp="3z0gdrikyw2j" />
                </div>
                <span className="text-[14px] text-white/60" data-id="element-1502" data-mp="dvvbe0fnaec2">
                  {isConnected ? sensor2Value : '--'}
                </span>
              </div>

              {/* Baby Activity */}
              <div className="bg-white/5 rounded-[16px] p-4 relative overflow-hidden" data-id="element-1503" data-mp="mxl0v0pyv377">
                <div className="flex justify-between items-center mb-4" data-id="element-1504" data-mp="rvmswb5decut">
                  <span className="text-[12px] text-white/50 uppercase tracking-[1px]" data-id="element-1505" data-mp="wnedm51uhod1">
                    Baby Activity 👶
                  </span>
                  <span className="text-[12px] text-white/50" data-id="element-1506" data-mp="11otvxpcn1grq">
                    Kicks this session:{' '}
                    <span className="text-white font-medium" data-id="element-1507" data-mp="16k0wwpeiw7cy">{kickCount}</span>
                  </span>
                </div>

                <div className="flex justify-center items-center h-[100px] relative" data-id="element-1508" data-mp="28bscngh0uvvm">
                  <AnimatePresence data-id="element-1509" data-mp="23d8t1mpwhw6f">
                    {showKickText && <motion.div initial={{
                opacity: 0,
                y: 10,
                x: 20
              }} animate={{
                opacity: 1,
                y: -20,
                x: 20
              }} exit={{
                opacity: 0
              }} className="absolute text-accent font-bold text-[14px] z-10" data-id="element-1510" data-mp="23b4kuskyyb0c">
                        +kick
                      </motion.div>}
                  </AnimatePresence>

                  <motion.div animate={!isConnected ? {
              scale: 1,
              opacity: 0.4
            } : showKick ? {
              scale: 1.05,
              x: 4,
              rotate: 3
            } : {
              scale: [1, 1.03, 1]
            }} transition={showKick ? {
              duration: 0.15,
              yoyo: 1
            } : {
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="relative" data-id="element-1511" data-mp="39ve079qt6ad6">
                    <svg width="80" height="80" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1512" data-mp="nm6k2sdk7viy">
                      {/* Stylized baby silhouette */}
                      <path d="M50 20C40 20 32 28 32 38C32 42 33.5 45.5 36 48.5C30 52 25 58 25 65C25 76 36 85 50 85C64 85 75 76 75 65C75 58 70 52 64 48.5C66.5 45.5 68 42 68 38C68 28 60 20 50 20Z" fill="#6B21E8" opacity="0.8" data-id="element-1513" data-mp="nio7cw8edlx3" />
                      <circle cx="45" cy="35" r="3" fill="white" opacity="0.6" data-id="element-1514" data-mp="9qq5n6h0msay" />
                      <path d="M40 42Q45 45 50 42" stroke="white" strokeWidth="2" strokeLinecap="round" opacity="0.6" data-id="element-1515" data-mp="3vxoc1vowxhp5" />
                      <path d="M35 65Q50 75 65 65" stroke="#00D4D4" strokeWidth="3" strokeLinecap="round" opacity="0.8" data-id="element-1516" data-mp="22g0h33rtmqzk" />
                    </svg>
                  </motion.div>
                </div>
              </div>
            </div>}

          {/* Bottom Controls */}
          {!isLandscape && <div className="mt-auto pt-6 lg:pt-12 w-full flex space-x-3 pb-[env(safe-area-inset-bottom)]" data-id="element-1517" data-mp="1nrvz620lejhu">
              <button onClick={() => setShowEndSession(true)} className="flex-1 h-[52px] bg-danger/20 border border-danger text-danger rounded-[14px] flex items-center justify-center space-x-2 font-medium" data-id="element-1518" data-mp="1sr8ulb5j4yw1">
                <Square size={18} className="fill-danger" data-id="element-1519" data-mp="1021mbkg9wuxl" />
                <span data-id="element-1520" data-mp="150mo3ob6bmo5">End Session</span>
              </button>
              <button className="w-[52px] h-[52px] bg-white/10 border border-white/20 text-white/80 rounded-[14px] flex items-center justify-center" aria-label="Share" data-id="element-1521" data-mp="14z1srwh1bzp8">
                <Share2 size={20} data-id="element-1522" data-mp="7eih7ztn9epw" />
              </button>
              <button onClick={() => navigate('/app/history')} className="w-[52px] h-[52px] bg-white/10 border border-white/20 text-white/80 rounded-[14px] flex items-center justify-center" aria-label="View history" data-id="element-1523" data-mp="7g3a7l0b8pln">
                <BarChart2 size={20} data-id="element-1524" data-mp="cd24c3jz4wju" />
              </button>
            </div>}
        </div>}

      {/* END SESSION BOTTOM SHEET */}
      <AnimatePresence data-id="element-1525" data-mp="m3obd9dmkeqx">
        {showEndSession && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowEndSession(false)} className="fixed inset-0 bg-black/60 z-[110]" data-id="element-1526" data-mp="m4g6kj3b8ago" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 bg-[#221A19] rounded-t-[24px] p-6 pb-[calc(24px+env(safe-area-inset-bottom))] z-[120] lg:max-w-[400px] lg:left-1/2 lg:-translate-x-1/2 lg:bottom-6 lg:rounded-[24px]" data-id="element-1527" data-mp="1jphe23cqlrh2">
              <div className="w-[40px] h-[4px] bg-white/20 rounded-pill mx-auto mb-6 lg:hidden" data-id="element-1528" data-mp="qzsgy135ebkq" />
              <h2 className="text-[20px] font-semibold text-white mb-2" data-id="element-1529" data-mp="d0izky37hbf6">
                End this session?
              </h2>
              <p className="text-[15px] text-white/60 mb-8" data-id="element-1530" data-mp="hzce8tu8b8gx">
                Your session data will be saved and synced. You can review it in
                History.
              </p>
              <div className="flex flex-col space-y-3" data-id="element-1531" data-mp="355vg72d5jq13">
                <button onClick={handleEndSession} disabled={isEndingSession} className="w-full h-[52px] bg-danger text-white rounded-[14px] font-medium flex items-center justify-center" data-id="element-1532" data-mp="3a3ww5efa798c">
                  {isEndingSession ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-1533" data-mp="3o3odnp50b7r3" /> : 'Yes, End Session'}
                </button>
                <button onClick={() => setShowEndSession(false)} disabled={isEndingSession} className="w-full h-[52px] bg-transparent text-white/70 rounded-[14px] font-medium" data-id="element-1534" data-mp="1od6epdtk8y1">
                  Keep Monitoring
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </main>;
}